import { type UserData } from '@beat-venues/common-types';

const mapObjectByKeys = <T extends object>(
    obj: T,
    keyOrder: (keyof T)[]
): T => {
    const sortedEntries = keyOrder
        .filter((key) => key in obj)
        .map((key) => [key, obj[key]]);

    const remainingEntries = Object.entries(obj).filter(
        ([key]) => !keyOrder.includes(key as keyof T)
    );

    return Object.fromEntries([...sortedEntries, ...remainingEntries]) as T;
};

const mapUserToAbbrev = (user: UserData | undefined) => {
    return typeof user === 'undefined'
        ? undefined
        : user.firstName
        ? user.firstName?.charAt(0).toUpperCase() +
          user.lastName?.charAt(0).toUpperCase()
        : user.email?.charAt(0).toUpperCase();
};

const mapUserToAccountForm = (user: UserData) => {
    const { mobilePhone, sfContactId, ...accountFormData } = user;

    return {
        ...accountFormData,
        mobilePhone: mobilePhone
            ? mobilePhone.startsWith('+')
                ? mobilePhone
                : `+${mobilePhone}`
            : '',
    };
};

const mapUserToUpdateForm = (user: UserData) => {
    const {
        country,
        street,
        postalCode,
        city,
        birthdate,
        firstName,
        lastName,
        language,
        gender,
        mobilePhone,
    } = user;
    return {
        birthdate,
        firstName,
        lastName,
        language,
        gender,
        mobilePhone,
        mailingAddress: {
            country,
            street,
            postalCode,
            city,
        },
    };
};

const sanitizeQueryParams = (searchParams: URLSearchParams) => {
    const safe: Record<string, string> = {};

    for (const [key, value] of searchParams.entries()) {
        safe[key] = value.trim().replace(/[<>"'`]/g, '');
    }

    return safe;
};

const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export {
    mapObjectByKeys,
    mapUserToAbbrev,
    mapUserToAccountForm,
    mapUserToUpdateForm,
    sanitizeQueryParams,
    isValidEmail,
};
